import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import SectionHeading from "@/components/base/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { principles, siteInfo, siteStats, testimonials } from "@/mocks/site";

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "只做线索的「第一公里」",
    desc: "我们不追热点、不抢首发。把一条零散的、没人愿意花时间看的线索，变成可以核查的材料，这才是我们存在的意义。",
  },
  {
    icon: "ri-user-shared-line",
    title: "为普通人留一扇门",
    desc: "知道内情的往往不是媒体从业者，而是每天在现场的人。我们要做的，是让这扇门足够低、足够安全。",
  },
  {
    icon: "ri-scales-3-line",
    title: "把判断权交还读者",
    desc: "我们陈述事实、标注来源、保留疑点，不替读者下结论，也不为了情绪而放大某个细节。",
  },
];

const editorialFlow = [
  { step: "01", title: "接收与初筛", desc: "值班编辑在 3 个工作日内判断线索是否具备核查方向。" },
  { step: "02", title: "交叉核实", desc: "至少两个独立来源印证，涉及数据时回溯原始凭证。" },
  { step: "03", title: "匿名化与撰写", desc: "处理所有可识别信息，再整理成可读的报道。" },
  { step: "04", title: "发布与跟进", desc: "发布后持续跟进，出现新证据就更新，发现错误更公开更正。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 | 直曝网络爆料投稿平台的定位与原则",
    description:
      "直曝是一个专注匿名线索收集与内容核查的网络爆料投稿平台。这里介绍我们的网站定位、发展理念、内容原则与编辑流程，以及为什么我们坚持先核实再发布。",
    keywords: "网络爆料,网络爆料投稿,直曝,匿名线索,内容核查",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于直曝",
        url: "https://ygtabln.cn/about",
        inLanguage: "zh-CN",
        description:
          "直曝是专注匿名线索收集与内容核查的网络爆料投稿平台，介绍网站定位、发展理念、内容原则与编辑流程。",
        mainEntity: {
          "@type": "Organization",
          name: siteInfo.fullName,
          url: "https://ygtabln.cn/",
          email: siteInfo.email,
          foundingDate: siteInfo.since,
          description: siteInfo.intro,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ygtabln.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://ygtabln.cn/about" },
        ],
      },
    ],
  });

  return (
    <>
      <div className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <Breadcrumbs items={[{ label: "关于我们" }]} />
            <h1 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              关于直曝
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              一个不爱起标题、只想把线索核实清楚的编辑团队。
            </p>
          </div>
        </div>
      </div>

      <section className="px-4 md:px-6 py-12 md:py-16" aria-labelledby="about-brand-title">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 id="about-brand-title" className="font-heading text-2xl md:text-[32px] font-bold text-foreground-950 leading-snug">
              {siteInfo.name}，从 {siteInfo.since} 年的一条投稿开始
            </h2>
            <div className="mt-5 space-y-4 text-[15px] md:text-base text-foreground-700 leading-[1.9]">
              <p>
                最开始，我们只是一个由几名编辑组成的小团队，用一条公开邮箱接收读者来信。第一篇报道，来自一位读者发来的一段三分钟的通话录音。
              </p>
              <p>
                那条线索最终促成了一笔迟到的退款。也是从那时起，我们意识到：很多人不是不想说，而是不知道该往哪里说、说了会不会被认出来。
              </p>
              <p>
                {siteInfo.intro}
              </p>
            </div>

            <dl className="mt-8 grid grid-cols-2 gap-5">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100 px-5 py-4">
                  <dd className="font-heading text-xl md:text-2xl font-bold text-primary-600">{stat.value}</dd>
                  <dt className="mt-1 text-xs md:text-sm text-foreground-600 font-label">{stat.label}</dt>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5">
            <div className="w-full h-64 md:h-full md:min-h-[420px] rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Quiet%20editorial%20desk%20scene%20with%20a%20notebook%20a%20pen%20and%20a%20cup%20of%20tea%20beside%20a%20small%20desk%20lamp%20in%20warm%20afternoon%20light%2C%20muted%20cream%20and%20soft%20vermillion%20tones%2C%20calm%20minimal%20atmosphere%2C%20no%20readable%20text%2C%20subtle%20film%20grain%2C%20high%20detail&width=800&height=1000&seq=about-zhibao-editorial-2026&orientation=portrait"
                alt="直曝编辑部的工作桌面与稿件"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100 border-y border-background-200" aria-labelledby="positioning-title">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            id="positioning-title"
            eyebrow="网站定位"
            title="我们不是什么，我们是什么"
            desc="一个平台的价值，往往在于它主动放弃了什么。"
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {positioning.map((item) => (
              <article key={item.title} className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
                <span className="w-12 h-12 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center">
                  <i className={`${item.icon} text-2xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-12 md:py-16" aria-labelledby="principles-title">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            id="principles-title"
            eyebrow="内容原则"
            title="六条不肯让步的规矩"
            desc="它们看起来朴素，但正是这些规矩，决定了每一条线索会被如何对待。"
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 p-5">
                <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center">
                  <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-3.5 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-12 md:py-16 bg-foreground-950" aria-labelledby="flow-title">
        <div className="max-w-6xl mx-auto">
          <p className="inline-flex items-center gap-2 text-xs font-label tracking-widest text-accent-400 uppercase">
            <span className="w-5 h-px bg-accent-500"></span>
            编辑流程
          </p>
          <h2 id="flow-title" className="mt-3 font-heading text-2xl md:text-[32px] font-bold text-background-50 leading-snug">
            一条线索在直曝的完整旅程
          </h2>
          <ol className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {editorialFlow.map((item) => (
              <li key={item.step} className="border-t border-background-50/20 pt-5">
                <span className="font-heading text-3xl font-bold text-accent-500">{item.step}</span>
                <h3 className="mt-3 font-heading text-lg font-bold text-background-50">{item.title}</h3>
                <p className="mt-2 text-sm text-background-300 leading-relaxed">{item.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-4 md:px-6 py-12 md:py-16" aria-labelledby="voice-title">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            id="voice-title"
            eyebrow="用户口碑"
            title="投稿人留下的几句话"
            desc="这些反馈被投稿人授权公开，姓名与身份信息已隐去。"
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((item) => (
              <figure key={item.role} className="rounded-lg bg-background-100 border border-background-200 p-5">
                <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true"></i>
                <blockquote className="mt-3 text-sm text-foreground-700 leading-relaxed">{item.quote}</blockquote>
                <figcaption className="mt-4 text-xs text-foreground-500 font-label">
                  {item.name} · {item.role}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 pb-16 md:pb-20" aria-labelledby="about-contact-title">
        <div className="max-w-6xl mx-auto rounded-lg bg-secondary-900 px-6 md:px-10 py-10 md:py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 id="about-contact-title" className="font-heading text-xl md:text-2xl font-bold text-secondary-50">
              想联系我们？
            </h2>
            <p className="mt-2.5 text-sm text-secondary-100/85 leading-relaxed max-w-xl">
              投稿、合作、纠错或删稿申请，都可以从联系页面开始。编辑邮箱：{" "}
              <a href={`mailto:${siteInfo.email}`} className="text-accent-400 hover:underline cursor-pointer break-all">
                {siteInfo.email}
              </a>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-background-50 text-foreground-950 text-sm font-label font-medium whitespace-nowrap hover:bg-accent-100 transition-colors cursor-pointer"
            >
              前往联系页面
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-secondary-50/35 text-secondary-50 text-sm font-label font-medium whitespace-nowrap hover:bg-secondary-50/10 transition-colors cursor-pointer"
            >
              查看常见问题
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}